import React, { useRef, useState, useEffect } from 'react';
import { GripVertical, Maximize2, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { WidgetMenu } from '@t-one-internal/design-system';
import type { ShareDestination } from '@t-one-internal/design-system';
import type { WidgetCapabilities, ChartType } from '../../types/analytics';
import { ScopingBadge } from './ScopingBadge';

export interface WidgetHeaderProps {
  title: string;
  widgetId: string | number;
  readOnly?: boolean;
  scoping?: string;
  templateKey?: string;

  /** Filter state */
  filterCount: number;
  hasFiltersAvailable: boolean;
  showFilters: boolean;
  onToggleFilters: () => void;

  /** Drill-down grid toggle */
  drillDownDataLength: number;
  showDrillDown: boolean;
  onToggleDrillDown: () => void;

  /** Expand modal */
  onExpand: () => void;

  /** Menu open state (controls opacity of controls) */
  isMenuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;

  /** Widget menu action callbacks */
  onClone?: () => void;
  onSendToDashboard?: (widgetId: string, targetDashboardId: number) => void;
  onFetchShareDestinations?: (widgetId: string) => Promise<ShareDestination[]>;
  onRemove?: () => void;
  capabilities?: WidgetCapabilities | null;
  currentChartType?: ChartType;
  onChartTypeChange?: (chartType: ChartType) => void;

  /** Title editing */
  onTitleChange?: (newTitle: string) => Promise<void>;
}

/**
 * WidgetHeader
 *
 * Self-contained header bar extracted from WidgetWrapper (lines 793–1057).
 * Handles drag handle, title editing, scoping badge, filter toggle, drill-down
 * toggle, widget menu, and expand button.
 */
export const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  title,
  widgetId,
  readOnly,
  scoping,
  templateKey,
  filterCount,
  hasFiltersAvailable,
  showFilters,
  onToggleFilters,
  drillDownDataLength,
  showDrillDown,
  onToggleDrillDown,
  onExpand,
  isMenuOpen,
  onMenuOpenChange,
  onClone,
  onSendToDashboard,
  onFetchShareDestinations,
  onRemove,
  capabilities,
  currentChartType,
  onChartTypeChange,
  onTitleChange,
}) => {
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Sync when parent title prop changes
  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  const handleTitleDoubleClick = () => {
    if (onTitleChange) {
      setIsEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== title && onTitleChange) {
      await onTitleChange(trimmedTitle);
    } else {
      setEditedTitle(title);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditedTitle(title);
      setIsEditingTitle(false);
    }
  };

  const handleSendToDashboard = (wId: string, targetDashboardId: number) => {
    if (onSendToDashboard) {
      onSendToDashboard(wId, targetDashboardId);
    }
  };

  const handleFetchShareDestinations = async (wId: string): Promise<ShareDestination[]> => {
    if (onFetchShareDestinations) {
      return onFetchShareDestinations(wId);
    }
    return [];
  };

  // mp_learning_plan hides the header entirely
  if (templateKey === 'mp_learning_plan') {
    return null;
  }

  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
      }}
      onMouseEnter={() => setIsHeaderHovered(true)}
      onMouseLeave={() => setIsHeaderHovered(false)}
    >
      {/* Left side: drag handle + title + scoping badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
        {/* Drag Handle — hidden in readOnly mode */}
        {!readOnly && (
          <div
            className="widget-drag-handle"
            style={{
              cursor: 'grab',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Drag to move"
          >
            <GripVertical size={16} style={{ color: '#6b7280' }} />
          </div>
        )}

        {/* Title — inline edit on double-click */}
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyDown}
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827',
              margin: 0,
              padding: '2px 6px',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              outline: 'none',
              backgroundColor: 'white',
              minWidth: '100px',
              maxWidth: '100%',
            }}
          />
        ) : (
          <h3
            onDoubleClick={handleTitleDoubleClick}
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827',
              margin: 0,
              overflow: isHeaderHovered ? 'hidden' : 'visible',
              textOverflow: isHeaderHovered ? 'ellipsis' : 'clip',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
              cursor: onTitleChange ? 'text' : 'default',
            }}
            title={onTitleChange ? 'Double-click to edit' : undefined}
          >
            {title}
          </h3>
        )}

        {/* Scoping badge */}
        {scoping && <ScopingBadge scoping={scoping} />}
      </div>

      {/* Right side: controls (hidden in readOnly) + expand button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        {/* Controls hidden in readOnly mode */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: !readOnly && (isHeaderHovered || isMenuOpen) ? 1 : 0,
            visibility: !readOnly && (isHeaderHovered || isMenuOpen) ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease, visibility 0.2s ease',
          }}
        >
          {/* Filter Toggle Button */}
          {hasFiltersAvailable && (
            <button
              onClick={onToggleFilters}
              style={{
                position: 'relative',
                padding: '4px 8px',
                backgroundColor: showFilters ? '#eff6ff' : 'transparent',
                border: showFilters ? '1px solid #bfdbfe' : '1px solid transparent',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!showFilters) {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilters) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              title={
                showFilters
                  ? 'Hide Filters'
                  : `${filterCount > 0 ? `${filterCount} filter${filterCount > 1 ? 's' : ''} applied - ` : ''}Show Filters`
              }
            >
              <Filter size={16} style={{ color: showFilters ? '#1e40af' : '#6b7280' }} />
              {/* Filter count badge */}
              {!showFilters && filterCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    minWidth: '14px',
                    height: '14px',
                    padding: '0 4px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '600',
                    borderRadius: '7px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {filterCount}
                </span>
              )}
            </button>
          )}

          {/* Details / drill-down toggle */}
          {drillDownDataLength > 0 && (
            <button
              onClick={onToggleDrillDown}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: showDrillDown ? '#dbeafe' : 'transparent',
                color: showDrillDown ? '#1e40af' : '#2563eb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              title="Toggle Details View"
            >
              {showDrillDown ? (
                <>
                  <ChevronUp size={14} />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Details
                </>
              )}
            </button>
          )}

          {/* Widget Menu */}
          <WidgetMenu
            widgetId={String(widgetId)}
            onClone={onClone}
            onSendToDashboard={handleSendToDashboard}
            onFetchShareDestinations={onFetchShareDestinations ? handleFetchShareDestinations : undefined}
            capabilities={capabilities}
            currentChartType={currentChartType}
            onChartTypeChange={onChartTypeChange}
            onOpenChange={onMenuOpenChange}
            onRemove={onRemove}
          />
        </div>

        {/* Expand button — always visible on hover, even in readOnly mode */}
        <div
          style={{
            opacity: isHeaderHovered || isMenuOpen ? 1 : 0,
            visibility: isHeaderHovered || isMenuOpen ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease, visibility 0.2s ease',
          }}
        >
          <button
            onClick={onExpand}
            style={{
              padding: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Expand Widget"
          >
            <Maximize2 size={16} style={{ color: '#6b7280' }} />
          </button>
        </div>
      </div>
    </div>
  );
};
